import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Shield, Search, CheckCircle, XCircle, Award } from 'lucide-react';

const VerifyCertificatePage = () => {
  const [certificateId, setCertificateId] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const mockCertificates = {
    'ICC2024-001': {
      valid: true,
      studentName: 'Rajesh Kumar',
      courseName: 'Ethical Hacking Certification',
      issueDate: '15-Dec-2024',
      instructor: 'Abhishek Parashar'
    },
    'ICC2024-002': {
      valid: true,
      studentName: 'Priya Sharma',
      courseName: 'Bug Bounty Hunter Program',
      issueDate: '10-Jan-2025',
      instructor: 'Abhishek Parashar'
    },
    'ICC2024-003': {
      valid: true,
      studentName: 'Amit Verma',
      courseName: 'SOC Analyst Training',
      issueDate: '05-Feb-2025',
      instructor: 'Abhishek Parashar'
    }
  };

  const handleVerify = () => {
    if (!certificateId.trim()) {
      return;
    }

    setIsVerifying(true);

    setTimeout(() => {
      const certificate = mockCertificates[certificateId.toUpperCase()];

      if (certificate) {
        setVerificationResult({
          valid: true,
          ...certificate
        });
      } else {
        setVerificationResult({
          valid: false,
          message: 'Certificate not found. Please check the Certificate ID and try again.'
        });
      }

      setIsVerifying(false);
    }, 1500);
  };

  const handleReset = () => {
    setCertificateId('');
    setVerificationResult(null);
  };

  return (
    <section className="py-16 sm:py-24 bg-gray-900 relative overflow-hidden min-h-screen pt-20 sm:pt-32">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">
              Certificate Verification
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-white mt-4 mb-6">
            Verify Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Certification
            </span>
          </h2>
          <p className="text-gray-400 text-lg">
            Enter your certificate ID to verify the authenticity of your Indian Cyber Club Academy certification
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="bg-gray-950/80 backdrop-blur-sm border border-red-600/30 p-8">
            <div className="space-y-6">
              {!verificationResult ? (
                <>
                  <div>
                    <label className="text-white font-semibold mb-3 block">
                      Certificate ID
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={certificateId}
                        onChange={(e) => setCertificateId(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                        placeholder="Enter Certificate ID (e.g., ICC2024-001)"
                        className="flex-1 bg-gray-900 border border-red-600/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                      />
                      <Button
                        onClick={handleVerify}
                        disabled={isVerifying || !certificateId.trim()}
                        className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isVerifying ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Verifying...
                          </>
                        ) : (
                          <>
                            <Search className="w-5 h-5 mr-2" />
                            Verify
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Try: ICC2024-001, ICC2024-002, or ICC2024-003
                    </p>
                  </div>

                  <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-400">
                        <p className="font-semibold text-white mb-1">How to find your Certificate ID?</p>
                        <p>Your Certificate ID is printed on your certificate. It follows the format: ICC[YEAR]-[NUMBER]</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  {verificationResult.valid ? (
                    <div className="space-y-6">
                      <div className="flex items-center justify-center gap-3 pb-6 border-b border-gray-800">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Certificate Verified!</h3>
                          <p className="text-green-500 font-semibold">Valid & Authentic</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-gray-500 block mb-1">Student Name</label>
                            <p className="text-white font-semibold text-lg">{verificationResult.studentName}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500 block mb-1">Certificate ID</label>
                            <p className="text-white font-semibold text-lg">{certificateId.toUpperCase()}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500 block mb-1">Course Name</label>
                            <p className="text-white font-semibold">{verificationResult.courseName}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500 block mb-1">Issue Date</label>
                            <p className="text-white font-semibold">{verificationResult.issueDate}</p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="text-sm text-gray-500 block mb-1">Issued By</label>
                            <p className="text-white font-semibold">{verificationResult.instructor} - Indian Cyber Club Academy</p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleReset}
                        variant="outline"
                        className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        Verify Another Certificate
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center justify-center gap-3 pb-6 border-b border-gray-800">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                          <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Verification Failed</h3>
                          <p className="text-red-500 font-semibold">Certificate Not Found</p>
                        </div>
                      </div>

                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="text-gray-300 text-center">{verificationResult.message}</p>
                      </div>

                      <Button
                        onClick={handleReset}
                        className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                      >
                        Try Again
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default VerifyCertificatePage;